import {
	keepPreviousData,
	type UseQueryResult,
	useQuery,
} from "@tanstack/react-query";
import { getEventsInRange } from "../../services/eventService";
import type { Event } from "../../types/event";

/**
 * TanStack Query hook for the patient events range (Phase 36 mocked, Phase 37 live).
 *
 * Query key shape `['events', from, to]` is LOCKED by C-04 — Phase 37's mutation
 * invalidation uses predicate `q.queryKey[0] === 'events'` to invalidate every
 * cached range at once (handles date-range cache fragmentation).
 *
 * For a single page render, `<TrackOverview>` and `<ComingUpFeed>` MUST share the
 * same `(from, to)` window so TanStack dedupes to a single query (RESEARCH.md Pitfall 3).
 */
export function useTrackEvents(
	from: string,
	to: string,
	opts?: { enabled?: boolean; staleTime?: number },
): UseQueryResult<Event[]> {
	return useQuery<Event[]>({
		queryKey: ["events", from, to] as const,
		queryFn: () => getEventsInRange(from, to),
		staleTime: opts?.staleTime ?? 60_000,
		refetchOnWindowFocus: true,
		enabled: opts?.enabled ?? true,
		// Keep prior window's events visible while the new range fetches; this
		// keeps `isLoading` false on paging so the global loader (driven by
		// `isFetching`) shows instead of the page-level skeleton.
		placeholderData: keepPreviousData,
	});
}
